import React from "react";
import { useParams } from "react-router-dom";
import { Loader } from "../../common/Loader";
import { formatDateAndTime } from "../../utils/date-utils";
import { FeedbackDetails, useFeedbackDetails } from "../../api/api-hooks";
import LlmResponseFormat from "../chats/LlmResponseFormat";

const FeedbackDetail: React.FC = () => {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const { data, isLoading, error } = useFeedbackDetails(feedbackId as string);

  if (isLoading) return <Loader />;
  if (error)
    return (
      <div className="px-4 py-3 text-red-600">Failed to load feedback.</div>
    );
  if (!data) return null;

  const details = data as FeedbackDetails;
  const dateAndTime = formatDateAndTime(details.created_at);
  const selected = Array.isArray(details.selected_feedback)
    ? details.selected_feedback
    : [];

  const badgeBase =
    "inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm";
  const goodBadge = "bg-emerald-100 text-emerald-800 border-emerald-300"; // ✅ “good” color
  const neutralBadge = "bg-gray-100 text-gray-800 border-gray-300";
  const dangerBadge = "bg-rose-100 text-rose-800 border-rose-300";

  const reactionClass =
    details.reaction === "like"
      ? goodBadge
      : details.reaction === "dislike"
      ? dangerBadge
      : neutralBadge;

  const reactionIcon =
    details.reaction === "like"
      ? "👍"
      : details.reaction === "dislike"
      ? "👎"
      : "•";

  return (
    <div className="flex flex-col w-[100%] mx-auto h-[100svh] md:h-screen">
      <div className="w-full text-center bg-white flex items-center h-fit justify-center py-4 pb-2 md:pb-4 border-b fixed z-10">
        <div className="w-full max-w-[700px] mx-auto flex-col flex lg:flex-row items-center justify-center">
          <div className="px-4 md:pl-0">
            <h2 className="text-primary m-0 text-[18px] leading-[25px] font-medium line-clamp-1">
              {details.app_name || "Feedback"}
            </h2>
          </div>
          <div className="text-[10px] md:text-[12px] leading-[14px] text-[#1E1E1E] font-light relative lg:absolute left-0 px-4">
            {details.username ?? "—"}&nbsp;&nbsp;
            <span className="h-full border-[0.5px] border-l-[#1E1E1E]" />
            &nbsp;&nbsp;{dateAndTime.date}
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-between h-full w-full px-4">
        <div className="h-[calc(100%-110px)] pt-14">
          <div className="h-full overflow-auto">
            <div className="w-full max-w-[800px] mx-auto py-4">
              {(selected.length > 0 || details.reaction) && (
                <div className="mb-3 flex flex-wrap items-center gap-2 px-4">
                  {selected.map((tag, i) => (
                    <span key={i} className={`${badgeBase} ${goodBadge}`}>
                      {tag}
                    </span>
                  ))}
                  {details.reaction && (
                    <span className={`${badgeBase} ${reactionClass}`}>
                      <span>{reactionIcon}</span>
                      <span className="capitalize">{details.reaction}</span>
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-start mx-0 md:mx-0 mt-2 px-4 py-2 font-light bg-[#EAEEF5] rounded-[20px] w-fit max-w-[90%] whitespace-pre-wrap">
                <p className="whitespace-pre-wrap">{details.prompt_text}</p>
              </div>

              <div className="max-w-[100%] rounded-normal rounded-bl-none bg-white md:bg-chatbg py-4 flex flex-col gap-0 mt-3 px-4">
                <div className="text-black text-lg flex gap-2">
                  <img src="/icons/logo.svg" alt="logo" className="w-[18px]" />
                  {details.llm ? details.llm.replace(/_/g, " ") : "Response"}
                </div>
                <div className="text-[14px] md:text-[16px] text-secondary font-normal leading-[22px]">
                  <div className="mt-0 text-content">
                    <LlmResponseFormat
                      generatedText={details.message_text || ""}
                      llm={details.llm || ""}
                      okrs={""}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDetail;
